/**
 * JourFx Internal Web Application
 * Main entry point with Convex + Better Auth + TanStack Router
 */
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./i18n";
import { ConvexReactClient } from "convex/react";
import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { authClient } from "./lib/auth";
import { routeTree } from "./routeTree.gen";
import { ThemeProvider } from "./components/ThemeProvider";

import { ErrorBoundary, SkipLink } from "@jourfx/ui";
import "./index.css";
import { applyBrandingFromCache } from "./branding/runtimeBranding";
import { BrandingProvider } from "./branding/BrandingProvider";

// Initialize Convex client
const convex = new ConvexReactClient(
  import.meta.env.VITE_CONVEX_URL || "http://localhost:3210"
);

// Apply cached branding early (before React mount) to reduce flashes.
// Reactive refresh is handled by BrandingProvider inside the component tree.
applyBrandingFromCache();

// Create router with context
// Note: Auth is handled in _authenticated.tsx component, not via router context
const router = createRouter({
  routeTree,
  context: {
    convex,
  },
  defaultPreload: "intent",
});

// Type declaration for router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// App wrapper
function AppWithAuth() {
  return <RouterProvider router={router} context={{ convex }} />;
}

// Loading fallback for i18n
function I18nLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

// Root app component
function App() {
  return (
    <Suspense fallback={<I18nLoadingFallback />}>
      <ThemeProvider>
        <ConvexBetterAuthProvider client={convex} authClient={authClient}>
          <BrandingProvider convex={convex}>
            <AppWithAuth />
          </BrandingProvider>
        </ConvexBetterAuthProvider>
      </ThemeProvider>
    </Suspense>
  );
}

// Mount the app - prevent duplicate root creation during HMR
const container = document.getElementById("root")!;
const root = (window as any).__REACT_ROOT__ || ReactDOM.createRoot(container);
(window as any).__REACT_ROOT__ = root;

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <SkipLink />
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
